'use client';

import { Badge, cn, HoverCard, HoverCardContent, HoverCardTrigger } from '@newfront-insurance/core-ui/v2';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { type ComponentProps, type ReactNode } from 'react';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { Streamdown, type Components } from 'streamdown';

import { Carousel, CarouselContent, CarouselItem, useCarousel } from '../ui/carousel';

export type InlineCitationProps = ComponentProps<'span'>;

export function InlineCitation({ className, ...props }: InlineCitationProps) {
  return <span data-nova-copy-exclude="true" className={cn('group inline items-center gap-1', className)} {...props} />;
}

export type InlineCitationTextProps = ComponentProps<'span'>;

export function InlineCitationText({ className, ...props }: InlineCitationTextProps) {
  return <span className={cn('group-hover:bg-accent transition-colors', className)} {...props} />;
}

export type InlineCitationCardProps = ComponentProps<typeof HoverCard>;

export function InlineCitationCard(props: InlineCitationCardProps) {
  return <HoverCard closeDelay={0} openDelay={0} {...props} />;
}

export type InlineCitationCardTriggerProps = ComponentProps<typeof Badge> & {
  additionalSourcesCount?: number;
  label: string;
};

export function InlineCitationCardTrigger({
  additionalSourcesCount,
  label,
  className,
  children,
  ...props
}: InlineCitationCardTriggerProps) {
  const triggerLabel = label.trim();
  const hiddenLabel = additionalSourcesCount
    ? `${triggerLabel}, ${additionalSourcesCount} additional source${additionalSourcesCount === 1 ? '' : 's'}`
    : triggerLabel;

  return (
    <HoverCardTrigger asChild>
      <Badge
        aria-label={hiddenLabel}
        data-testid="inline-citation-trigger"
        className={cn(
          'border-steel-75 bg-steel-50 text-steel-500 hover:bg-steel-100 inline-flex',
          'max-w-52 min-w-0 cursor-pointer items-center justify-start gap-2 rounded-full border',
          'px-2 py-0.5 text-center text-[12px] leading-4 font-normal whitespace-nowrap shadow-none',
          className,
        )}
        variant="secondary"
        {...props}
      >
        {children ?? (
          <>
            <span className="max-w-44 min-w-0 truncate text-left">{triggerLabel}</span>
            {additionalSourcesCount ? (
              <span className="text-steel-400 shrink-0 font-semibold">+{additionalSourcesCount}</span>
            ) : null}
          </>
        )}
      </Badge>
    </HoverCardTrigger>
  );
}

export type InlineCitationCardBodyProps = ComponentProps<typeof HoverCardContent>;

export function InlineCitationCardBody({
  className,
  collisionPadding = 16,
  align = 'start',
  ...props
}: InlineCitationCardBodyProps) {
  return (
    <HoverCardContent
      className={cn(
        'border-steel-200 relative min-h-[180px] w-[342px] overflow-hidden rounded-[6px] bg-white p-0 shadow-[0_5px_5px_rgba(0,0,0,0.03)]',
        className,
      )}
      collisionPadding={collisionPadding}
      align={align}
      {...props}
    />
  );
}

export type InlineCitationCarouselProps = ComponentProps<typeof Carousel>;

export function InlineCitationCarousel({ className, children, ...props }: InlineCitationCarouselProps) {
  return (
    <Carousel className={cn('w-full', className)} {...props}>
      {children}
    </Carousel>
  );
}

export type InlineCitationCarouselContentProps = ComponentProps<'div'>;

export function InlineCitationCarouselContent(props: InlineCitationCarouselContentProps) {
  return <CarouselContent {...props} />;
}

export type InlineCitationCarouselItemProps = ComponentProps<'div'>;

export function InlineCitationCarouselItem({ className, ...props }: InlineCitationCarouselItemProps) {
  return <CarouselItem className={cn('w-full space-y-0 px-[11px] pt-[6px] pb-3', className)} {...props} />;
}

export type InlineCitationCarouselPreviousProps = ComponentProps<'button'>;

export function InlineCitationCarouselPrevious({ className, ...props }: InlineCitationCarouselPreviousProps) {
  const { scrollPrev, canScrollPrev } = useCarousel();

  return (
    <button
      type="button"
      data-testid="inline-citation-carousel-previous"
      className={cn(
        'text-steel-400 hover:text-steel-500 disabled:text-steel-200 flex size-4 items-center justify-center transition-colors',
        className,
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <ChevronLeft className="size-4" />
    </button>
  );
}

export type InlineCitationCarouselNextProps = ComponentProps<'button'>;

export function InlineCitationCarouselNext({ className, ...props }: InlineCitationCarouselNextProps) {
  const { scrollNext, canScrollNext } = useCarousel();

  return (
    <button
      type="button"
      data-testid="inline-citation-carousel-next"
      className={cn(
        'text-steel-400 hover:text-steel-500 disabled:text-steel-200 flex size-4 items-center justify-center transition-colors',
        className,
      )}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <ChevronRight className="size-4" />
    </button>
  );
}

/**
 * Compact markdown components for citation snippets.
 * Only overrides elements that need structural changes (block → inline).
 * Styling for standard elements (lists, tables, code, etc.) is handled
 * by Tailwind's prose classes on the wrapper div.
 */
const snippetMarkdownComponents: Partial<Components> = {
  // Convert block elements to inline for compact snippet display
  p: ({ children }) => <span>{children}</span>,
  h1: ({ children }) => <strong className="font-semibold">{children}</strong>,
  h2: ({ children }) => <strong className="font-semibold">{children}</strong>,
  h3: ({ children }) => <strong className="font-semibold">{children}</strong>,
  h4: ({ children }) => <strong className="font-semibold">{children}</strong>,
  h5: ({ children }) => <strong className="font-semibold">{children}</strong>,
  h6: ({ children }) => <strong className="font-semibold">{children}</strong>,
  blockquote: ({ children }) => <span className="italic">{children}</span>,
  pre: ({ children }) => <span className="bg-muted rounded px-1">{children}</span>,
  // Links need target="_blank" for external citation sources
  a: ({ children, href }) => (
    <a href={href} target="_blank" rel="noreferrer">
      {children}
    </a>
  ),
  // Override Streamdown's table defaults which add a heavy wrapper with
  // double borders and hardcoded text-sm sizing. Plain elements let prose
  // handle the styling at the correct text-xs size.
  table: ({ children }) => <table>{children}</table>,
  thead: ({ children }) => <thead>{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => <tr>{children}</tr>,
  th: ({ children }) => <th>{children}</th>,
  td: ({ children }) => <td>{children}</td>,
};

const snippetRemarkPlugins = [remarkGfm];
const snippetRehypePlugins = [rehypeRaw];

export type InlineCitationSourceProps = ComponentProps<'div'> & {
  title?: string;
  url?: string;
  description?: string;
  metadata?: ReactNode;
};

export function InlineCitationSource({
  title,
  url,
  description,
  metadata,
  className,
  children,
  ...props
}: InlineCitationSourceProps) {
  return (
    <div className={cn('relative space-y-1.5', className)} {...props}>
      {title && <h4 className="text-steel-500 pr-6 text-[14px] leading-5 font-semibold break-words">{title}</h4>}
      {metadata}
      {description && (
        <div className="prose prose-sm prose-a:text-brand-400 prose-thead:bg-muted text-steel-500 mt-2 max-w-none text-[12px] leading-4">
          <Streamdown
            components={snippetMarkdownComponents}
            remarkPlugins={snippetRemarkPlugins}
            rehypePlugins={snippetRehypePlugins}
            controls={false}
          >
            {description}
          </Streamdown>
        </div>
      )}
      {children}
    </div>
  );
}

export type InlineCitationQuoteProps = ComponentProps<'blockquote'>;

export function InlineCitationQuote({ children, className, ...props }: InlineCitationQuoteProps) {
  return (
    <blockquote
      className={cn('border-muted text-muted-foreground border-l-2 pl-3 text-sm italic', className)}
      {...props}
    >
      {children}
    </blockquote>
  );
}
